import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { assign } from 'lodash';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { ReplacementCostService } from '../replacement-cost.service';
import { CommonDropDown, SingleCommonDropDown } from 'app/core/common/types/dropdown.types';
import { LookupService } from 'app/core/common/services/lookup.service';
import { ReplacementCost } from '../replacement-cost.types';

@Component({
    selector: 'replacement-cost-details',
    templateUrl: './replacement-cost-details.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReplacementCostDetailsComponent implements OnInit, OnDestroy {

    replacementCost: ReplacementCost;
    replacementCostForm: FormGroup;
    dialogTitle = `New ReplacementCost`
    alert: any;
    reportType: string;
    cost: number;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    options: CommonDropDown[];
    filteredOptions: Observable<CommonDropDown[]>;


    replacementCostFilterForm: FormGroup;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) private _data: {
            replacementCost: ReplacementCost,
            reportType: string
        },
        private replacementCostService: ReplacementCostService,
        private _matDialogRef: MatDialogRef<ReplacementCostDetailsComponent>,
        private _formBuilder: FormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertMessage: AlertMessageService,
        private _lookupService: LookupService
    ) {
        this.reportType = this._data.reportType;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Open the drawer
        this.replacementCostForm = this._formBuilder.group({
            id: [''],
            reportId: [this._data.replacementCost.reportId],
            type: [this._data.replacementCost.type, [Validators.required]],
            // type: ['', [Validators.required]],
            area: [this._data.replacementCost.area],
            rate: [this._data.replacementCost.rate],
            cost: [this._data.replacementCost.cost],
            orderIndex: [this._data.replacementCost.orderIndex],
        });

        if (this._data.replacementCost.id) {
            // Request the data from the server
            this.replacementCostService.getReplacementCost(this._data.replacementCost.id)
                .subscribe((res) => {
                    if (res.isSuccess) {
                        this.replacementCost = res.result;
                        this.dialogTitle = `Edit ReplacementCost`;
                        this.replacementCostForm.patchValue(this.replacementCost);
                    }
                    else {
                        this._alertMessage.error(res.errors.join('\n'));
                        this.onClose();
                    }
                });
        }
        this.replacementCostForm.valueChanges
            .pipe(
                debounceTime(500),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                if (value.area && value.rate)
                    this.replacementCostForm.get('cost').patchValue(value.area * value.rate);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Close the drawer
     */
    // closeDrawer(): Promise<MatDrawerToggleResult> {
    //     return this._optionTypesListComponent.matDrawer.close();
    // }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
    setResetForm(oldData: ReplacementCost) {
        this._data.replacementCost as ReplacementCost;
        this._data.replacementCost.reportId = oldData.reportId;
        this._data.replacementCost.orderIndex = oldData.orderIndex + 1;
        this.replacementCostForm.reset();
        this.replacementCostForm.patchValue(this._data.replacementCost);
    }
    saveAndNew() {
        const replacementCost = this.replacementCostForm.getRawValue();
        this.save(replacementCost, true)
    }
    saveAndClose() {
        const replacementCost = this.replacementCostForm.getRawValue();
        this.save(replacementCost);

    }
    save(replacementCost, isOpen = false) {
        this.replacementCostService.createReplacementCost(replacementCost).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("ReplacementCost added successfully");
                // Close the dialog
                if (!isOpen) {
                    this._matDialogRef.close(response);
                }
                else {
                    this.setResetForm(replacementCost);
                }
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }
    updateAndClose() {

        const replacementCost = this.replacementCostForm.getRawValue();

        this.replacementCostService.updateReplacementCost(replacementCost).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("ReplacementCost updated successfully");
                // Close the dialog
                this._matDialogRef.close(response);
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }

    onClose(): void {
        this._matDialogRef.close();
    }

    displayOptionsFn(options: CommonDropDown[]): (name: string) => string {
        return (name: string) => {
            const correspondingOption = Array.isArray(options) ? options.find(option => option.name === name) : null;
            return correspondingOption ? correspondingOption.name : '';
        }
    }

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter(option => option.name.toLowerCase().includes(filterValue));
    }
}
